import { z } from 'zod';
/**
 * The operational state of the deployment, as the console renders it — INF-04.
 *
 * WHY THIS IS A CONTRACT AND `/health` IS NOT. The public `/health` route has no
 * schema here on purpose: its consumer is a free external prober that reads the
 * STATUS CODE and nothing else, so a body shape it never parses would be a
 * contract with no second party. This one has a screen behind it, and every
 * other screen in the console is typed from this package.
 *
 * WHAT THE SHAPES ARE PERMISSIVE ABOUT, and why that is not laziness: `aiSvc`
 * and the heartbeat payload are produced by a FastAPI service and a sibling
 * container respectively, both of which can be deployed independently of this
 * package (ADR-0005). Parsing them strictly would mean a field added to
 * `ai-svc`'s health body could make the console's status screen fail — which is
 * to say the diagnostic surface would break in exactly the situation it exists
 * for. Unknown keys are carried through and ignored.
 */
/* -------------------------------------------------------------------------- */
/* Dependencies                                                               */
/* -------------------------------------------------------------------------- */
/**
 * The same four booleans the public `/health` computes, and deliberately the
 * same four: two surfaces disagreeing about whether the worker is alive is worse
 * than either of them being wrong.
 */
export const HealthDependencies = z.object({
    database: z.boolean(),
    valkey: z.boolean(),
    aiSvc: z.boolean(),
    worker: z.boolean(),
});
/**
 * `ai-svc`'s own `/health` body, as much of it as the console draws.
 *
 * `.passthrough()` and every field optional: this is another service's payload.
 * `null` when it did not answer at all — which is a different fact from
 * answering with `status: "degraded"`, and the screen says so differently.
 */
export const AiSvcDetail = z
    .object({
    status: z.string().optional(),
    adapters: z.record(z.boolean()).optional(),
    faceModelLoaded: z.boolean().optional(),
    embedModelLoaded: z.boolean().optional(),
})
    .passthrough();
/* -------------------------------------------------------------------------- */
/* Worker                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * The `worker` unit, via the Valkey key it writes on a TTL.
 *
 * `ageSeconds` IS COMPUTED ON THE SERVER, and that is the one field here that is
 * not simply relayed. The heartbeat carries an ISO `at` written by a different
 * container; subtracting it from a browser's clock would show a phone with a
 * skewed time a worker that died in the future. The api reads both from the same
 * process, so the difference it computes is the only one anybody should trust.
 *
 * Null throughout when the key has expired — absence is the signal, exactly as
 * `worker-heartbeat.service.ts` documents it.
 */
export const WorkerHealth = z.object({
    alive: z.boolean(),
    ageSeconds: z.number().nullable(),
    at: z.string().nullable(),
    pid: z.number().nullable(),
    /** Queue name → whether its BullMQ consumer is attached and running. */
    consumers: z.record(z.boolean()).nullable(),
    /** Sampled every ~5 minutes by the worker, not probed on this request. */
    spaces: z.boolean().nullable(),
    spacesCheckedAt: z.string().nullable(),
});
/* -------------------------------------------------------------------------- */
/* Queues                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * One BullMQ queue's depth.
 *
 * DIAGNOSTIC ONLY, AND NOTHING HERE SETS A STATUS CODE — the same rule the
 * heartbeat states, restated because this is the surface where somebody will
 * first be tempted to break it. A backlog is a load condition: 500 photos at
 * 09:00 is a Tuesday, and the threshold that is right then is wrong at 03:00.
 *
 * `oldestWaitingMs` is here because DEPTH ALONE CANNOT BE READ. A busy worker
 * that is keeping up has a deep queue whose head is young; a stalled one has a
 * head that keeps ageing whatever the depth does. It is null when nothing is
 * waiting, which is not the same as zero and must not render as "0 ms".
 */
export const QueueHealth = z.object({
    name: z.string(),
    waiting: z.number(),
    active: z.number(),
    delayed: z.number(),
    failed: z.number(),
    oldestWaitingMs: z.number().nullable(),
});
/**
 * A recently failed job, for the panel that answers "why did photos stop".
 *
 * NO `stacktrace`, DELIBERATELY. `failedReason` names the failure; a stack trace
 * names our directory layout, our dependency versions and our internal module
 * paths. This route is admin-only, so the exposure is small — but it is not zero
 * and it buys nothing a log line does not, and the person reading this screen
 * has access to the logs.
 *
 * Job payloads are ids only (see `queue.module.ts`), so no personal data reaches
 * here even though the jobs are about photographs of people.
 */
export const FailedJob = z.object({
    id: z.string(),
    queue: z.string(),
    name: z.string(),
    /** ISO. When the job was enqueued, not when it failed. */
    timestamp: z.string(),
    failedReason: z.string(),
    attemptsMade: z.number(),
});
/* -------------------------------------------------------------------------- */
/* Photo analysis failures                                                    */
/* -------------------------------------------------------------------------- */
/**
 * WHICH STEP OF THE PIPELINE THREW — the whole reason the failure log exists.
 *
 * `photo.processor.ts` runs eight distinct operations inside one `try`, and
 * every one of them reached the field as the same sentence: "Analisis AI gagal
 * setelah beberapa percobaan." A bug in planogram scoring is not an AI failure,
 * and telling an administrator that it was costs an hour of looking in the wrong
 * service.
 *
 * The members are the steps in the order they run:
 *
 *   LOAD_CONTEXT  reading the photo row and its visit
 *   PRESIGN       minting the GET url ai-svc fetches the image with
 *   AI_ANALYSE    POST /analyse — the model call itself
 *   AI_EMBED      POST /embed — appearance matching for unnamed detections
 *   ARCHIVE       writing the provider transcript to object storage
 *   PERSIST       the analysis and detection rows
 *   FANOUT        `photo.analysed` subscribers: scoring and the price index
 *   PUBLISH       the SSE frame and the audit row
 *   UNKNOWN       an error that escaped every wrapper — a bug in the wrapping
 *
 * UNKNOWN IS A MEMBER ON PURPOSE. A stage the code failed to tag must be
 * greppable in the data rather than crashing the insert that records it; a
 * failure log that can itself fail is worth less than no failure log.
 *
 * AI_EMBED AND ARCHIVE ARE ALMOST UNREACHABLE, AND AN EMPTY BUCKET THERE IS NOT
 * EVIDENCE OF HEALTH. Both `matchByAppearance` and `archiveRawPayload` catch
 * their own failures and carry on — deliberately, since neither appearance
 * matching nor a training transcript is worth failing an analysis a user is
 * waiting for. So an embedding outage shows up as detections that stay
 * unnamed, not as rows here, and this log will keep reporting zero throughout
 * it. The two stages are wired anyway because they are on the path and a future
 * edit could stop swallowing; do not read their silence as a signal.
 */
export const PhotoFailureStage = z.enum([
    'LOAD_CONTEXT',
    'PRESIGN',
    'AI_ANALYSE',
    'AI_EMBED',
    'ARCHIVE',
    'PERSIST',
    'FANOUT',
    'PUBLISH',
    'UNKNOWN',
]);
/**
 * One failed attempt at analysing one photograph.
 *
 * ONE ROW PER ATTEMPT, NOT PER PHOTO, and `terminal` is what separates them. A
 * photo that times out once and reads fine on the retry writes a row with
 * `terminal: false` and nothing else — which is precisely the signal that says
 * ai-svc is degrading, days before it degrades enough to fail all three attempts
 * and become visible to a salesperson.
 *
 * NO STACK TRACE ON THE CONTRACT, for the reason `FailedJob` above gives at
 * length: it names our directory layout, our dependency versions and our
 * internal module paths, and buys nothing over a log line for somebody who has
 * log access. The stack IS captured — `photo_analysis_failures.error_detail`
 * holds it — but it is read from psql, not served to a browser. Do not add it
 * here because a screen would be marginally more convenient with it.
 *
 * `visitId` and `outletName` are null when the failure happened at
 * `LOAD_CONTEXT`: there was no context to name the visit with, which is itself
 * the diagnosis.
 */
export const PhotoAnalysisFailure = z.object({
    id: z.string().uuid(),
    /** ISO. When the attempt failed, not when the photo was taken. */
    occurredAt: z.string(),
    photoId: z.string().uuid(),
    visitId: z.string().uuid().nullable(),
    outletName: z.string().nullable(),
    regionName: z.string().nullable(),
    /** 1-based, matching BullMQ's `attemptsMade + 1`. */
    attempt: z.number().int(),
    /** True when the job gave up rather than backing off for another try. */
    terminal: z.boolean(),
    stage: PhotoFailureStage,
    retryable: z.boolean(),
    /** The error's constructor name, e.g. `AiSvcError`, `TypeError`. */
    errorClass: z.string(),
    errorMessage: z.string(),
    /** ai-svc's HTTP status when the stage was one of the two calls to it. */
    aiSvcStatus: z.number().int().nullable(),
    durationMs: z.number().int().nullable(),
});
/**
 * Failures per stage over a recent window, for the panel that leads the screen.
 *
 * A COUNT IS THE ANSWER TO A DIFFERENT QUESTION THAN A LIST. "Seventeen
 * failures, fourteen of them in AI_ANALYSE" is diagnosable at a glance; the
 * same fact recovered by paging through seventeen rows and tallying them is not.
 * Stages with no failures in the window are absent rather than zero — the screen
 * renders what happened, not the whole taxonomy.
 */
export const PhotoFailureStageCount = z.object({
    stage: PhotoFailureStage,
    count: z.number().int(),
    /** ISO. The most recent failure in this stage within the window. */
    lastAt: z.string(),
});
/** `GET /system/photo-failures/summary`. */
export const PhotoFailureSummary = z.object({
    /** How far back the counts reach, so the screen can label itself honestly. */
    windowHours: z.number().int(),
    /** Attempts that failed in the window, terminal or not. */
    total: z.number().int(),
    /** Of those, the ones where the job gave up for good. */
    terminal: z.number().int(),
    /** Distinct photographs affected — smaller than `total` when retries failed too. */
    photos: z.number().int(),
    byStage: z.array(PhotoFailureStageCount),
});
/* -------------------------------------------------------------------------- */
/* The response                                                               */
/* -------------------------------------------------------------------------- */
/**
 * `GET /system/health`.
 *
 * IT IS ALWAYS 200, INCLUDING WHEN `status` IS `degraded`, and that is the
 * difference between this route and the one the prober watches. `api.get()`
 * throws on a non-2xx, so a 503 here would empty the screen at precisely the
 * moment somebody opened it to find out what broke. The status code is the
 * public route's contract; this one's contract is the body.
 */
export const SystemHealth = z.object({
    status: z.enum(['ok', 'degraded']),
    /** Server clock at the moment of the read, so the screen can age its own data. */
    checkedAt: z.string(),
    dependencies: HealthDependencies,
    aiSvc: AiSvcDetail.nullable(),
    worker: WorkerHealth,
    queues: z.array(QueueHealth),
    recentFailures: z.array(FailedJob),
});
//# sourceMappingURL=health.js.map